import { Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  AccountCustomizationContent,
  AccountCustomizationData,
  Customizations,
  useAccountCustomizationsStore
} from '../../../../../store/AccountCustomization';
import { DetailViewCard } from '../../../../Compounds/CardWithTitle';
import { SettingsModalContent } from '../../../UserCreationPage/SettingsModalContent';
import { useEffect, useState } from 'react';
import { PostAccountCustomizationRequest } from '../../../../../Api/Preferences/AccountCustomizations/accountCustomizationsApi';
import { useSnackbar } from '../../../../../Context/SnackbarContext';
import { GridPreferences, OrderRequestPreferences, Widgets } from '../../../../../store/User';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../../../utils/styleHelpers';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { DisableCustomizationsForm } from '../../../../Compounds/DisableCustomizationsForm/DisableCustomizationsForm';
import { CustomFieldsForm } from '../../../../Compounds/CustomFieldsForm/CustomFieldsForm';
import { fetchCustomFieldDefinitionsApi } from '../../../../../Api/Admin/CustomFields/customFieldDefinitionsApi';
import { CustomFieldDefinition } from '../../../../../types';

export interface CustomizationsParams {
  data: AccountCustomizationData;
}

// An account that never set a default-on setting has it on, so its first toggle turns it off.
const toggleDefaultOn = (value?: boolean): boolean => value === false;

export const AccountCustomizations = ({ data }: CustomizationsParams) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newCustomizations, setNewCustomizations] = useState<AccountCustomizationData>(
    JSON.parse(JSON.stringify(data))
  );
  const [title, setTitle] = useState<string>('');
  const [customFieldDefinitions, setCustomFieldDefinitions] = useState<CustomFieldDefinition[]>([]);
  const { updateRequest, createRequest, fetchAccountCustomizations } = useAccountCustomizationsStore();
  const { setSnack } = useSnackbar();

  useEffect(() => {
    setNewCustomizations(JSON.parse(JSON.stringify(data)));
  }, [data.account_id]);

  useEffect(() => {
    if (!data.account_id) return;
    fetchCustomFieldDefinitionsApi(data.account_id).then(({ data: defs, error }) => {
      if (defs) setCustomFieldDefinitions(defs);
      if (error) setSnack({ message: 'Failed to load custom fields.', open: true, type: 'error' });
    });
  }, [data.account_id]);

  const handleEditColumns = () => {
    setOpen(true);
    setTitle('Inventory');
  };

  const handleEditWidgets = () => {
    setOpen(true);
    setTitle('Widgets');
  };

  const handleEditOrderRequest = () => {
    setOpen(true);
    setTitle('order_request');
  };

  const handleEditCustomFields = () => {
    setOpen(true);
    setTitle('custom_fields');
  };

  const handleSetGridPreferences = (prefs: GridPreferences) => {
    const update = { ...newCustomizations };
    update.content.inventory_grid = prefs;
    setNewCustomizations(update);
  };

  const handleCancel = () => {
    setNewCustomizations(JSON.parse(JSON.stringify(data)));
    setTitle('');
    setOpen(false);
  };

  const saveContent = async (content: AccountCustomizationContent): Promise<boolean> => {
    if (data.account_id) {
      setLoading(true);
      const req: PostAccountCustomizationRequest = {
        account_id: data.account_id,
        account_name: data?.account_name || '',
        content
      };

      const res = data?.id ? await updateRequest(data?.id, req, title) : await createRequest(req);

      if (res?.error) {
        setSnack({
          message: res.error.message,
          open: true,
          type: 'error'
        });
      } else {
        setTitle('');
        setOpen(false);
        setSnack({
          message: 'Settings Saved',
          open: true,
          type: 'success'
        });

        await fetchAccountCustomizations();
      }
      setLoading(false);
      return !res?.error;
    }
    return false;
  };

  const handleSave = () => saveContent(newCustomizations.content);

  const widgetsDefault = () => {
    const newWidgets = newCustomizations?.content?.widgets?.home;
    return newWidgets?.every((w) => w?.disabled === false);
  };

  const columnsDefault = () => {
    const disabledFields = newCustomizations?.content?.inventory_grid?.disabledFields;
    return disabledFields?.length === 0;
  };

  const orderRequestDefault = () => {
    // If the advanced form setting is set or any disabled product families exist, then custom settings are applied
    const orderRequestSettings = newCustomizations?.content?.order_request;
    const isDefault =
      !orderRequestSettings ||
      (!orderRequestSettings.isAdvancedForm && orderRequestSettings.disabledProductFamilies?.length === 0);
    return isDefault;
  };

  const customFieldsDefault = () => {
    return customFieldDefinitions.filter((d) => !d.inherited && !d.is_archived).length === 0;
  };

  const handleWidgetChange = (customizations: Customizations) => {
    const update = { ...newCustomizations };
    update.content.widgets = customizations as Widgets;
    setNewCustomizations(update);
  };

  const handleOrderRequestChange = (customizations: Customizations) => {
    const update = { ...newCustomizations };
    update.content.order_request = customizations as OrderRequestPreferences;
    setNewCustomizations(update);
  };

  const saveToggle = async (key: 'comments' | 'supplier_contacts' | 'non_recurring_inventory', next: boolean) => {
    const stored = newCustomizations.content[key];
    const content = { ...newCustomizations.content, [key]: next };
    setNewCustomizations({ ...newCustomizations, content });
    if (!(await saveContent(content))) {
      // The state can move on while the save is in flight, so only this switch is put back.
      setNewCustomizations((current) => ({ ...current, content: { ...current.content, [key]: stored } }));
    }
  };

  const handleCommentChange = () => saveToggle('comments', toggleDefaultOn(newCustomizations.content.comments));

  const handleSupplierContactsChange = () =>
    saveToggle('supplier_contacts', !newCustomizations.content.supplier_contacts);

  const handleNonRecurringInventoryChange = () =>
    saveToggle('non_recurring_inventory', toggleDefaultOn(newCustomizations.content.non_recurring_inventory));

  return (
    <DetailViewCard
      collapsed={false}
      dataCy="account-customizations-card"
      title="Customizations"
      enableCollapse>
      <div className="w-11/12 m-auto">
        <SettingsModalContent
          columnsCustom={!columnsDefault()}
          onEditColumns={handleEditColumns}
          widgetsCustom={!widgetsDefault()}
          onEditWidgets={handleEditWidgets}
          handleCommentChange={handleCommentChange}
          handleSupplierContactsChange={handleSupplierContactsChange}
          handleNonRecurringInventoryChange={handleNonRecurringInventoryChange}
          newCustomizations={newCustomizations}
          orderRequestCustom={!orderRequestDefault()}
          onEditOrderRequest={handleEditOrderRequest}
          customFieldsCustom={!customFieldsDefault()}
          onEditCustomFields={handleEditCustomFields}
        />
      </div>
      <Dialog
        open={open}
        maxWidth={title === 'custom_fields' ? 'md' : 'sm'}
        fullWidth={title === 'custom_fields'}
        sx={{
          height: '50rem'
        }}
        onClose={() => {
          setTitle('');
          setOpen(false);
        }}
        data-testid="edit-account-settings-modal">
        <IconButton
          data-testid="customizations-close-button"
          data-cy="account-customizations-modal-close-button"
          aria-label="close"
          onClick={() => {
            setTitle('');
            setOpen(false);
          }}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}>
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ padding: '2rem' }}>
          {title === 'Inventory' && newCustomizations && (
            <DisableCustomizationsForm
              type="column"
              header="Table Customizations"
              subheader="Enable and disable table columns for the users you're creating."
              gridPrefs={newCustomizations.content.inventory_grid || []}
              className="flex"
              saveHandler={(v) => handleSetGridPreferences(v as GridPreferences)}
            />
          )}
          {title === 'Widgets' && (
            <DisableCustomizationsForm
              type="widget"
              header="User Widget Customizations"
              subheader="Enable and disable widgets for the users you're creating."
              widgets={newCustomizations.content.widgets || []}
              className="flex"
              saveHandler={handleWidgetChange}
            />
          )}
          {title === 'order_request' && (
            <DisableCustomizationsForm
              type="order_request"
              header="Order Request Settings"
              subheader="Disable/Enable the Product Families you'd like the Account to have access to in the Order Request Form. Then select whether you'd like the Account to view the standard or Advanced version of the form."
              orderRequestPreferences={newCustomizations.content.order_request}
              className="flex"
              saveHandler={handleOrderRequestChange}
            />
          )}
          {title === 'custom_fields' && data.account_id && (
            <CustomFieldsForm
              accountId={data.account_id}
              onSave={async () => {
                const { data: defs, error } = await fetchCustomFieldDefinitionsApi(data.account_id!);
                if (error) {
                  setSnack({ message: 'Custom Fields saved but failed to refresh.', open: true, type: 'error' });
                } else {
                  if (defs) setCustomFieldDefinitions(defs);
                  setSnack({ message: 'Custom Fields Saved', open: true, type: 'success' });
                }
                setTitle('');
                setOpen(false);
              }}
            />
          )}
        </DialogContent>
        <DialogActions className="flex mx-4 mb-3 space-x-4">
          {loading && <ComponentSpinner label="Saving..." />}
          {!loading && title !== 'custom_fields' && (
            <>
              <button
                className={DEFAULT_CANCEL_BUTTON_STYLE}
                data-testid="account-customizations-cancel"
                data-cy="account-customizations-modal-cancel"
                onClick={handleCancel}>
                Cancel
              </button>
              <button
                className={DEFAULT_ADVANCE_BUTTON_STYLE}
                onClick={handleSave}>
                Save
              </button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </DetailViewCard>
  );
};
